import { useState } from "react";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import { CreateInternalTaskDialog } from "./CreateInternalTaskDialog";
import type { InternalProject } from "@/hooks/use-my-tasks";
import type { InternalProjectTask } from "@/hooks/use-internal-project-tasks";

/**
 * VIB-1208: ett internprosjekt sin gruppe i oppgavevelgeren — prosjektnavn
 * som header, oppgavene som velgbare rader, og en «+ Ny oppgave»-knapp.
 * Egen fil for å holde NewRegistrationPopup/RegistrationEditDialog under
 * filstørrelsesgrensen (endringssjekk-notat på VIB-1208).
 */
export function InternalTaskPickerGroup({
  project,
  tasks,
  activeIdx,
  startIdx,
  onSelectTask,
  onHoverIdx,
}: {
  project: InternalProject;
  tasks: InternalProjectTask[];
  activeIdx: number;
  /** Index i det globale flatItems-arrayet der denne gruppens første oppgave starter. */
  startIdx: number;
  onSelectTask: (task: InternalProjectTask) => void;
  onHoverIdx: (idx: number) => void;
}) {
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between px-3 xl:px-4 pt-2.5 pb-1">
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
          {project.name}
        </p>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setCreateOpen(true);
          }}
          className="text-[10px] text-primary hover:underline flex items-center gap-0.5"
        >
          <SrIcon name="plus" className="w-3 h-3" />
          Ny oppgave
        </button>
      </div>
      {tasks.length === 0 && (
        <p className="text-[10px] text-muted-foreground px-3 xl:px-4 pb-1.5">
          Ingen oppgaver ennå
        </p>
      )}
      {tasks.map((t, i) => {
        const itemIdx = startIdx + i;
        const isActive = itemIdx === activeIdx;
        return (
          <button
            key={t.id}
            data-idx={itemIdx}
            onClick={() => onSelectTask(t)}
            onMouseEnter={() => onHoverIdx(itemIdx)}
            className={cn(
              "w-full flex items-center gap-2 px-3 xl:px-4 py-2 text-left transition-colors group",
              isActive ? "bg-muted" : "hover:bg-muted/50",
            )}
          >
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{t.description}</p>
              <p className="text-[10px] text-muted-foreground">Internt prosjekt</p>
            </div>
            <SrIcon
              name="chevron-right"
              className={cn(
                "w-3.5 h-3.5 text-muted-foreground flex-shrink-0 transition-opacity",
                isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
              )}
            />
          </button>
        );
      })}

      <CreateInternalTaskDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        projectId={project.id}
        projectName={project.name}
      />
    </div>
  );
}
